/*
 * The browser tab's title: one rule for every page and state, so a person with several tabs open can
 * tell them apart. Nothing else writes `document.title`; the shell writes this, and a page only says
 * what it has open (`useTabTitle`).
 *
 *   [what is open] · [the place] · [the workspace] · LeadCue
 *
 * Most specific first, because a narrow tab shows only its start. What is open: a conversation, named
 * by the lead, not by the subject its head leads with (the subject is the campaign's own in most of a
 * campaign's threads, so it cannot tell two tabs apart); a campaign; a settings section; or "Could
 * not load" for a page that failed. The place is the row the rail marks as current, so the tab and
 * the rail never disagree.
 *
 * Home, Inbox, Meeting requests and Needs reply carry the work waiting on us: the totals those two
 * rail rows show, added, read off the rows so the tab and the rail cannot disagree (it once counted
 * unseen mail, 204 beside a rail saying 208, a number on no row). Since LEA-494 the two rows split
 * what needs a reply between them, so their sum is the whole of it, the one number (LEA-485): never
 * on another view, never zero, never a guess while the counts are loading or failed, and no favicon
 * badge or flashing.
 *
 * Before there is a page the title is the state instead: "Sign in", "Could not load", "No access",
 * "Workspace unavailable", and plain "LeadCue" while loading, which is how every tab starts.
 */
import { createContext, type Dispatch, type SetStateAction, useContext, useEffect } from "react";
import { INBOX_SECTION, type ShellNav, type ShellPage, workTotal } from "../shell-nav.ts";

const PRODUCT = "LeadCue";

/** What the shell draws in place of a page; `page` once there is one (`ShellGate`). */
export type ShellGateKind =
  | "loading"
  | "session_failed"
  | "sign_in"
  | "refused"
  | "workspaces_failed"
  | "unavailable"
  | "page";

const GATE_TITLE: Record<Exclude<ShellGateKind, "page">, string | null> = {
  loading: null,
  session_failed: "Could not load",
  sign_in: "Sign in",
  refused: "No access",
  workspaces_failed: "Could not load",
  unavailable: "Workspace unavailable",
};

/** A page's place before the rail has a row for it: no workspace yet, or a row this person lacks. */
const PAGE_PLACE: Record<ShellPage, string> = {
  inbox: "Inbox",
  mail: "Inbox",
  settings: "Settings",
  answers: "Standing answers",
  campaigns: "Campaigns",
  campaign: "Campaigns",
  mailboxes: "Mailboxes",
  account: "Account",
};

/** Said in place of what a page has open when the page itself failed to load. */
export const FAILED_PAGE = "Could not load";

export type Place = { readonly label: string; readonly total: number | null };

/** The places whose tab carries the work's total: home, and the two rows that split the work. */
const COUNTED_PLACES = ["inbox", "meeting", "needs_reply"];

/** The rail's current row; on Inbox, home, Meeting requests and Needs reply, with the work's total. */
export function placeOf(nav: ShellNav | null, page: ShellPage): Place {
  // The Inbox section is current on every inbox page; the place is the row inside it, or the page.
  const sections = nav?.sections.filter((row) => row.key !== INBOX_SECTION) ?? [];
  const rows = nav ? [...nav.mail, ...nav.views, ...sections, nav.settings] : [];
  const current = rows.find((row) => row?.current === true);
  if (!current) return { label: PAGE_PLACE[page], total: null };
  const counted =
    page === "mail" && COUNTED_PLACES.includes(current.key) && nav?.mail.includes(current) === true;
  return { label: current.label, total: counted && nav ? workTotal(nav.mail) : null };
}

export type TabTitleInput = {
  readonly gate: ShellGateKind;
  readonly open: string | null;
  readonly place: Place;
  readonly workspace: { readonly name: string } | null;
};

/** One line of text however the name was typed: a tab has no room for a line break. */
const oneLine = (text: string): string => text.replace(/\s+/g, " ").trim();

export function tabTitle(input: TabTitleInput): string {
  if (input.gate !== "page") {
    const state = GATE_TITLE[input.gate];
    return state ? `${state} · ${PRODUCT}` : PRODUCT;
  }
  const { label, total } = input.place;
  const place = total ? `${label} (${total.toLocaleString("en-US")})` : label;
  return [input.open, place, input.workspace?.name ?? null, PRODUCT]
    .map((part) => (part ? oneLine(part) : ""))
    .filter((part) => part !== "")
    .join(" · ");
}

/** The shell's setter for what the page has open; outside a shell, nothing listens. */
export const TabTitleContext = createContext<Dispatch<SetStateAction<string | null>>>(() => {});

/**
 * Names what this page has open in the tab's title, or nothing with null. A page leaving takes its
 * name with it, unless the next page has already put its own in.
 */
export function useTabTitle(open: string | null): void {
  const set = useContext(TabTitleContext);
  useEffect(() => {
    set(open);
    return () => set((shown) => (shown === open ? null : shown));
  }, [set, open]);
}
